import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';

import { PeopleAccessLayout, RequirePermission } from './components';
import { DEFAULT_TAB_ID, PERMISSIONS } from './constants';
import {
  GroupDetailPage,
  GroupsPage,
  MemberDetailPage,
  MemberInvitePage,
  MembersImportPage,
  MembersListPage,
  RoleDetailPage,
  RoleNewPage,
  RolesPage,
} from './pages';
import { getBasePath } from './utils';

/** Wraps a screen in the route guard (hide → redirect, lock → no-access state; CT-5569). */
const guarded = (permission, page) => (
  <RequirePermission permission={permission}>{page}</RequirePermission>
);

/**
 * People & Access routes, relative to the `people-access/*` mount of the host
 * (Core Shell or the standalone bootstrap). The three list screens share the
 * header + tab layout; every other screen is full page.
 * Every screen is guarded by its permission (CT-5569).
 */
const PeopleAccess = () => {
  const { pathname } = useLocation();
  const params = useParams();
  const basePath = getBasePath(pathname, params['*']);
  const defaultTabPath = `${basePath}/${DEFAULT_TAB_ID}`;

  return (
    <Routes>
      <Route element={<PeopleAccessLayout basePath={basePath} />}>
        <Route index element={<Navigate to={defaultTabPath} replace />} />
        <Route path="members" element={guarded(PERMISSIONS.MEMBERS_VIEW, <MembersListPage />)} />
        <Route path="roles" element={guarded(PERMISSIONS.ROLES_VIEW, <RolesPage basePath={basePath} />)} />
        <Route path="groups" element={guarded(PERMISSIONS.GROUPS_VIEW, <GroupsPage />)} />
      </Route>
      <Route path="members/invite" element={guarded(PERMISSIONS.MEMBERS_INVITE, <MemberInvitePage />)} />
      <Route path="members/import" element={guarded(PERMISSIONS.MEMBERS_INVITE, <MembersImportPage />)} />
      <Route path="members/:memberId/:tab?" element={guarded(PERMISSIONS.MEMBERS_VIEW, <MemberDetailPage basePath={basePath} />)} />
      <Route path="roles/new" element={guarded(PERMISSIONS.ROLES_MANAGE, <RoleNewPage basePath={basePath} />)} />
      <Route path="roles/:roleId" element={guarded(PERMISSIONS.ROLES_VIEW, <RoleDetailPage basePath={basePath} />)} />
      <Route path="groups/:groupId" element={guarded(PERMISSIONS.GROUPS_VIEW, <GroupDetailPage />)} />
      <Route path="*" element={<Navigate to={defaultTabPath} replace />} />
    </Routes>
  );
};

export default PeopleAccess;
